import { arrayRemove, arrayUnion, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '.';
import { termsVersion } from '../content/legal';

import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getIdTokenResult,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword
} from 'firebase/auth';
import { startHasHostedVenues } from './venues';

// Firebase reports failures as codes like 'auth/user-not-found', and its raw
// error.message reads 'Firebase: Error (auth/user-not-found).' — not something
// to show a venue host. Translate the codes we can actually hit into plain
// English, and fall back to the caller's message for anything unmapped.
const AUTH_ERROR_MESSAGES = {
  'auth/invalid-email': 'That email address does not look right. Please check it and try again.',
  'auth/missing-email': 'Please enter your email address.',
  'auth/user-not-found': 'We could not find an account with that email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact support@yedding.com.',
  'auth/wrong-password': 'That password is not correct.',
  'auth/missing-password': 'Please enter your password.',
  'auth/invalid-credential': 'That email and password do not match. If you signed up with Google or Facebook, use that button instead.',
  'auth/invalid-login-credentials': 'That email and password do not match. If you signed up with Google or Facebook, use that button instead.',
  'auth/email-already-in-use': 'An account already uses that email address. Please sign in instead.',
  'auth/weak-password': 'Please choose a longer password. Use at least 6 characters.',
  'auth/requires-recent-login': 'Please sign out and sign in again before you make this change.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'We could not reach the server. Please check your internet connection and try again.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support@yedding.com.',
  'auth/popup-closed-by-user': 'You closed the sign-in window before it finished.',
  'auth/cancelled-popup-request': 'A sign-in window is already open. Please wait a moment and try again.',
  'auth/popup-blocked': 'Your browser blocked the sign-in window. Please allow pop-ups and try again.',
  'auth/account-exists-with-different-credential':
    'An account already uses that email address, but with a different sign-in method. Try Google, Facebook, or your password.'
};

export function getAuthErrorMessage(error, defaultMessage) {
  if (typeof error === 'string') return error;
  if (error && error.code && AUTH_ERROR_MESSAGES[error.code]) return AUTH_ERROR_MESSAGES[error.code];
  return defaultMessage;
}

function handleError(setError, defaultMessage) {
  return (error) => {
    // Keep the raw error in the console for debugging; show people the plain one.
    if (error && error.code) console.error(error.code, error.message);
    setError(getAuthErrorMessage(error, defaultMessage));
  };
}

export const resetUserPassword = (email, setSuccess, setError) => {
  sendPasswordResetEmail(auth, email).then(setSuccess).catch(handleError(setError, 'We could not send the reset email. Please try again.'));
};

export const signInUser = ({ email, password }, setSuccess, setError) => {
  signInWithEmailAndPassword(auth, email, password).then(setSuccess).catch(handleError(setError, 'We could not sign you in. Please try again.'));
};

export const signOutUser = (setSuccess, setError) => {
  signOut(auth).then(setSuccess).catch(handleError(setError, 'We could not sign you out. Please try again.'));
};

// Billing B4.4: the record of a "yes" to the terms. Firestore rules accept it
// only with the server time and the sign-in method in the user's token.
const termsRecord = async (user) => ({
  at: serverTimestamp(),
  version: termsVersion(),
  method: (await getIdTokenResult(user)).signInProvider
});

// Billing B4.4: Google and Facebook make the sign-in account before we can
// ask. While a new account is on the terms step, it has no user record yet.
let termsStepOpen = false;
export const setTermsStepOpen = (open) => {
  termsStepOpen = open;
};

/** Billing B4.4: the user record for a new Google or Facebook account, after the tick. */
export const createSocialUser = async (user) => {
  const nameParts = (user.displayName || '').trim().split(' ');
  await setDoc(doc(db, 'users', user.uid), {
    uid: user.uid,
    email: user.email ?? '',
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    createdAt: new Date().toISOString(),
    termsAccepted: await termsRecord(user)
  });
};

/**
 * Billing B4.4: a Google or Facebook account with no user record never
 * finished the terms step (e.g. the tab was closed). Sign it out, so the next
 * sign-in shows the step again. Email accounts write their record at once.
 */
export const signOutUnfinishedAccount = async (user) => {
  if (termsStepOpen || !user || user.providerData.some((p) => p.providerId === 'password')) return;
  if (auth.currentUser && auth.currentUser.uid === user.uid) await signOut(auth);
};

export const signUpUser = async ({ socialId, email, password, firstName, lastName }, setSuccess, setError) => {
  try {
    // Create the user with email & password
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Save additional profile data in Firestore
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      firstName,
      lastName,
      email,
      socialId,
      registerTime: Date.now(),
      termsAccepted: await termsRecord(user),
      savedVenues: [],
      // legacy fields
      reservedVenues: [],
      chats: []
    });

    // Signal success to the caller
    if (setSuccess) setSuccess();

    // Send email verification
    await sendEmailVerification(user);
    // (You could notify the user that a verification email was sent.)
  } catch (error) {
    console.error('Error creating account:', error);
    if (setError) setError(getAuthErrorMessage(error, 'We could not create your account. Please try again.'));
  }
};

export const getUserData = async (setUserData, callback, onMissing) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('There is no authenticated user');
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userDocRef);

    if (!snap.exists()) {
      console.log('User document does not exist');
      if (onMissing) onMissing(user);
      return;
    }

    const userObject = {
      id: user.uid,
      ...snap.data()
    };

    // Calcula el flag de “hasVenues” (igual que antes)
    startHasHostedVenues({
      setHasVenues(hasVenues) {
        userObject.hasVenues = hasVenues;
        setUserData(userObject);
        if (callback) callback(userObject);
      }
    });
  } catch (err) {
    console.error('Error getting user data:', err);
  }
};
export const updateUserEmail = (email, setSuccess, setError) => {
  updateEmail(auth.currentUser, email).then(setSuccess).catch(handleError(setError, 'We could not update your email address. Please try again.'));
};

export const updateUserPassword = async ({ email, currentPassword, nextPassword }, setSuccess, setError) => {
  const user = auth.currentUser;
  if (!user) {
    setError('Please sign in again before you change your password.');
    return;
  }

  // Firebase requires a recent login before it will accept a new password.
  // Re-authenticate in place rather than signing in again: a second sign-in
  // would churn the auth state and re-trigger every onAuthStateChanged listener.
  try {
    const credential = EmailAuthProvider.credential(user.email || email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  } catch (error) {
    if (error && error.code) console.error(error.code, error.message);
    // With email enumeration protection on, a wrong password comes back as
    // invalid-credential rather than wrong-password. Both mean the same thing
    // here, and the shared map's wording for them is written for sign-in.
    const code = error && error.code;
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials') {
      setError('Your current password is not correct.');
    } else {
      setError(getAuthErrorMessage(error, 'We could not confirm your current password. Please try again.'));
    }
    return;
  }

  try {
    await updatePassword(user, nextPassword);
    if (setSuccess) setSuccess();
  } catch (error) {
    handleError(setError, 'We could not update your password. Please try again.')(error);
  }
};

export const updateUserProfile = async ({ email, phoneNumber, firstName, lastName }, setSuccess, setError) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user found');

    // Reference the user's document
    const userRef = doc(db, 'users', user.uid);

    // Update the specified fields
    await updateDoc(userRef, {
      email,
      phoneNumber,
      firstName,
      lastName
    });

    // Notify caller of success
    if (setSuccess) setSuccess();
  } catch (error) {
    console.error('Failed to update profile:', error);
    if (setError) setError('Failed to update profile');
  }
};

// Billing B2.1: the booking box saves the phone number, in E.164 form, before checkout.
export const savePhoneNumber = async (phoneNumber) => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user found');
  await updateDoc(doc(db, 'users', user.uid), { phoneNumber });
};

export const addSavedVenue = async (venueId, setUserData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user found');

    const userRef = doc(db, 'users', user.uid);

    // Add the venueId to the savedVenues array
    await updateDoc(userRef, {
      savedVenues: arrayUnion(venueId)
    });

    // Fetch the updated user document
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      console.warn('User document does not exist after update');
      return;
    }

    const userObject = {
      id: user.uid,
      ...snap.data()
    };

    setUserData(userObject);
  } catch (error) {
    console.error('Error adding venue to favorites or fetching user data:', error);
  }
};

export const removeSavedVenue = async (venueId, setUserData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user found');

    const userRef = doc(db, 'users', user.uid);

    // Remove the venueId from the savedVenues array
    await updateDoc(userRef, {
      savedVenues: arrayRemove(venueId)
    });

    // Fetch the updated user document
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      console.warn('User document does not exist after update');
      return;
    }
  } catch (error) {
    console.error('Error removing venue from favorites or fetching user data:', error);
  }
};

// export const signUpUser = ({ socialId, email, password, firstName, lastName }, setSuccess, setError) => {
//   auth
//     .createUserWithEmailAndPassword(email, password)
//     .then((userCredentials) => {
//       const { user } = userCredentials;
//       db.collection('users')
//         .doc(user.uid)
//         .set({
//           firstName,
//           lastName,
//           email,
//           socialId,
//           registerTime: Date.now(),
//           savedVenues: [],
//           // legacy fields:
//           reservedVenues: [],
//           chats: []
//         })
//         .then(setSuccess)
//         .catch(() => {
//           setError('Failed to create account');
//         });
//       auth.currentUser.sendEmailVerification().then(() => {
//         // Email verification sent!
//       });
//     })
//     .catch(handleError(setError, 'Failed to create account'));
// };

// export const getUserData = (setUserData, callback) => {
//   const user = auth.currentUser;

//   db.collection('users')
//     .doc(user.uid)
//     .get()
//     .then((doc) => {
//       const userObject = {
//         id: user.uid,
//         ...doc.data()
//       };
//       // calculate host attributes
//       startHasHostedVenues({
//         setHasVenues(hasVenues) {
//           userObject.hasVenues = hasVenues;
//           setUserData(userObject);
//           if (callback) callback(userObject);
//         }
//       });
//     })
//     .catch(() => {
//       console.error('There was an error getting user data');
//     });
// };

// export const updateUserProfile = ({ email, phoneNumber, firstName, lastName }, setSuccess, setError) => {
//   const currentID = auth.currentUser.uid;
//   db.collection('users')
//     .doc(currentID)
//     .update({
//       email,
//       phoneNumber,
//       firstName,
//       lastName
//     })
//     .then(setSuccess)
//     .catch(handleError(setError, 'Failed to update profile'));
// };

// export const addSavedVenue = (venueId, setUserData) => {
//   // write venueId into the users SavedVenue array
//   const currentID = auth.currentUser.uid;

//   db.collection('users')
//     .doc(currentID)
//     .update({
//       savedVenues: arrayUnion(venueId)
//     })
//     .then(() => {
//       db.collection('users')
//         .doc(currentID)
//         .get()
//         .then((doc) => {
//           const userObject = {
//             id: currentID,
//             ...doc.data()
//           };
//           setUserData(userObject);
//         })
//         .catch(() => {
//           console.error('There was an error getting user data');
//         });
//     })
//     .catch(() => {
//       console.error('There was an error adding venue to favorites');
//     });
// };

// export const removeSavedVenue = (venueId, setUserData) => {
//   // remove venueId from users SavedVenue array
//   const currentID = auth.currentUser.uid;

//   db.collection('users')
//     .doc(currentID)
//     .update({
//       savedVenues: arrayRemove(venueId)
//     })
//     .then(() => {
//       db.collection('users')
//         .doc(currentID)
//         .get()
//         .then((doc) => {
//           const userObject = {
//             id: currentID,
//             ...doc.data()
//           };
//           setUserData(userObject);
//         })
//         .catch(() => {
//           console.error('There was an error getting user data');
//         });
//     })
//     .catch(() => {
//       console.error('There was an error removing venue from favorites');
//     });
// };
